import {
	Box,
	Center,
	Collapse,
	createStyles,
	getStylesRef,
	Group,
	Loader,
	Stack,
	UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { IconNames } from '@repo/foundations';
import { Icon, Text } from '@repo/foundations';
import type { ReactNode } from 'react';
import { memo } from 'react';

const useStyles = createStyles((theme, { isOpen }: { isOpen: boolean }) => ({
	container: {
		[`&:hover .${getStylesRef('chevron')}`]: {
			opacity: 1,
		},
	},
	chevron: {
		ref: getStylesRef('chevron'),
		color: theme.other.getColor('icon/secondary/default'),
		transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
		transition: 'opacity 150ms ease-in-out',
		opacity: 0,
	},
}));

export const ReasoningCollapse = memo(
	({
		title,
		children,
		icon,
		isLoading,
	}: {
		title: string;
		children?: ReactNode;
		icon?: IconNames;
		isLoading?: boolean;
	}) => {
		const [isOpen, { toggle }] = useDisclosure(false);
		const { classes } = useStyles({ isOpen });

		if (!children) {
			return (
				<Group spacing="2xs" noWrap py="xs" align="flex-start">
					<Group spacing={0} noWrap>
						<Box className={classes.chevron}>
							<Icon name="chevronDown" />
						</Box>
						{isLoading ? (
							<Center p={2}>
								<Loader size={16} />
							</Center>
						) : (
							<Icon color="icon/brand/default" name={icon || 'brain'} />
						)}
					</Group>
					<Text size="sm" weight="semibold">
						{title}
					</Text>
				</Group>
			);
		}

		return (
			<Stack spacing={0}>
				<UnstyledButton onClick={toggle} className={classes.container}>
					<Group spacing="2xs" noWrap py="xs" align="flex-start">
						<Group spacing={0} noWrap>
							<Box className={classes.chevron}>
								<Icon name="chevronDown" />
							</Box>
							{isLoading ? (
								<Center p={2}>
									<Loader size={16} />
								</Center>
							) : (
								<Icon color="icon/brand/default" name={icon || 'brain'} />
							)}
						</Group>
						<Text size="sm" weight="semibold">
							{title}
						</Text>
					</Group>
				</UnstyledButton>

				<Collapse in={isOpen} pl={20} pb="xs">
					{children}
				</Collapse>
			</Stack>
		);
	}
);
